/**
 * Bar heights for the waveform of a voice message, normalized 0..1. Mirrors what the mobile
 * player draws (`apps/mobile/src/ui/blocks/audio-player.tsx`) so a recording looks the same in
 * every client.
 *
 * `metering` is the dB envelope (-50..0) the mobile recorder stores on the attachment; a message
 * recorded elsewhere has none and gets an organic, voice-like fallback instead of a flat line.
 */
export const AUDIO_WAVE_BAR_COUNT = 26;

/** Quiet parts still read as a bar, not a dot. */
const MIN_BAR = 0.38;

const FALLBACK_SHAPE = Array.from({ length: AUDIO_WAVE_BAR_COUNT }, (_, i) => {
  const envelope = Math.sin((i / AUDIO_WAVE_BAR_COUNT) * Math.PI); // louder in the middle
  const detail = (Math.sin(i * 1.7) * 0.5 + 0.5) * 0.5 + (Math.sin(i * 0.6 + 2) * 0.5 + 0.5) * 0.5;
  return MIN_BAR + (1 - MIN_BAR) * (0.45 + 0.55 * envelope) * (0.6 + 0.4 * detail);
});

/** dB (-50..0) → MIN_BAR..1, piecewise-linear through -20 dB → 0.7 like the mobile player. */
const decibelsToBar = (decibels: number): number => {
  if (decibels <= -50) return MIN_BAR;
  if (decibels >= 0) return 1;
  if (decibels <= -20) return MIN_BAR + ((decibels + 50) / 30) * (0.7 - MIN_BAR);
  return 0.7 + ((decibels + 20) / 20) * (1 - 0.7);
};

export const buildAudioWaveBars = (metering?: number[] | null): number[] => {
  if (!metering || metering.length === 0) {
    return FALLBACK_SHAPE;
  }

  return Array.from({ length: AUDIO_WAVE_BAR_COUNT }, (_, i) => {
    const from = Math.floor((i * metering.length) / AUDIO_WAVE_BAR_COUNT);
    const to = Math.max(from + 1, Math.ceil(((i + 1) * metering.length) / AUDIO_WAVE_BAR_COUNT));
    const slice = metering.slice(from, to);
    const average = slice.reduce((sum, value) => sum + value, 0) / slice.length;
    return decibelsToBar(average);
  });
};
